/**
 * Web3Context - Unified wallet, network, and contract management for React.
 *
 * Features:
 * - EIP-6963 wallet detection (MetaMask preference)
 * - Arbitrum One network management with auto-add
 * - ABI loading from backend API
 * - Contract factory with signer/read-only modes
 * - Silent auto-connect if previously authorized
 * - Event listeners for account/chain changes
 * - Backend session synchronization
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode
} from 'react';
import {
  BrowserProvider,
  JsonRpcProvider,
  Contract,
  type Signer,
  type Eip1193Provider,
  type InterfaceAbi
} from 'ethers';

// ============================================================================
// Types
// ============================================================================

interface ExtendedEip1193Provider extends Eip1193Provider {
  isMetaMask?: boolean;
  providers?: Eip1193Provider[];
  on?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
}

interface NetworkConfig {
  chainId: number;
  chainIdHex: string;
  chainName: string;
  rpcUrls: string[];
  blockExplorerUrls: string[];
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

interface Web3ContextType {
  provider: JsonRpcProvider | BrowserProvider | null;
  providerReadOnly: JsonRpcProvider | null;
  signer: Signer | null;
  account: string | null;
  chainId: number | null;
  isConnected: boolean;
  isConnecting: boolean;
  isInitialized: boolean;
  abis: Record<string, InterfaceAbi>;
  getAbi: (name: string) => InterfaceAbi;
  getContract: (address: string, abi: InterfaceAbi, mode?: 'auto' | 'signer' | 'readOnly') => Contract;
  connect: () => Promise<string | null>;
  disconnect: () => Promise<void>;
  switchNetwork: () => Promise<void>;
  contractAddress: string;
  network: NetworkConfig;
}

// ============================================================================
// RetryProvider
// ============================================================================

class RetryProvider extends JsonRpcProvider {
  private fallbacks: JsonRpcProvider[];
  private urls: string[];

  constructor(urls: string[]) {
    super(urls[0]);
    this.urls = urls;
    this.fallbacks = urls.slice(1).map(url => new JsonRpcProvider(url));
  }

  async send(method: string, params: Array<any>): Promise<any> {
    try {
      return await super.send(method, params);
    } catch (primaryError: any) {
      if (primaryError?.code === 'CALL_EXCEPTION') {
        throw primaryError;
      }
      for (let i = 0; i < this.fallbacks.length; i++) {
        try {
          console.warn(
            `[Web3] RPC #1 (${this.urls[0]}) failed for ${method}, ` +
            `trying #${i + 2} (${this.urls[i + 1]})...`
          );
          return await this.fallbacks[i].send(method, params);
        } catch {
          // Continue to next fallback
        }
      }
      throw primaryError;
    }
  }
}

// ============================================================================
// Constants
// ============================================================================

const ARBITRUM_CONFIG: NetworkConfig = {
  chainId: 42161,
  chainIdHex: '0xa4b1',
  chainName: 'Arbitrum One',
  rpcUrls: [
    'https://arb1.arbitrum.io/rpc',
    'https://arbitrum-one.publicnode.com',
    'https://1rpc.io/arb'
  ],
  blockExplorerUrls: ['https://arbiscan.io/'],
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18
  }
};

// ============================================================================
// Context
// ============================================================================

const Web3Context = createContext<Web3ContextType | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

export const Web3Provider = ({ children }: { children: ReactNode }) => {
  const [providerReadOnly, setProviderReadOnly] = useState<JsonRpcProvider | null>(null);
  const [provider, setProvider] = useState<JsonRpcProvider | BrowserProvider | null>(null);
  const [signer, setSigner] = useState<Signer | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [abis, setAbis] = useState<Record<string, InterfaceAbi>>({});
  const [contractAddress, setContractAddress] = useState<string>('');

  // Wallet Detection (EIP-6963 + legacy)
  const selectInjectedProvider = useCallback((preferMetaMask = true): ExtendedEip1193Provider | null => {
    const candidates: ExtendedEip1193Provider[] = [];

    try {
      const handleAnnounce = (ev: CustomEvent) => {
        if (ev.detail?.provider) {
          candidates.push(ev.detail.provider);
        }
      };
      window.addEventListener('eip6963:announceProvider', handleAnnounce as EventListener, { once: true });
      window.dispatchEvent(new Event('eip6963:requestProvider'));
    } catch {
      // EIP-6963 not supported
    }

    if ((window as any).ethereum) {
      const list = (window as any).ethereum.providers?.length
        ? (window as any).ethereum.providers
        : [(window as any).ethereum];
      candidates.push(...list);
    }

    if (preferMetaMask) {
      const mm = candidates.find((p: any) => p?.isMetaMask);
      if (mm) return mm;
    }

    return candidates[0] || null;
  }, []);

  // Network Management
  const switchNetwork = useCallback(async () => {
    const injected = selectInjectedProvider();
    if (!injected) return;

    const browserProvider = new BrowserProvider(injected);

    try {
      await browserProvider.send('wallet_switchEthereumChain', [
        { chainId: ARBITRUM_CONFIG.chainIdHex }
      ]);
    } catch (err: any) {
      if (err?.code === 4902) {
        await browserProvider.send('wallet_addEthereumChain', [{
          chainId: ARBITRUM_CONFIG.chainIdHex,
          chainName: ARBITRUM_CONFIG.chainName,
          nativeCurrency: ARBITRUM_CONFIG.nativeCurrency,
          rpcUrls: ARBITRUM_CONFIG.rpcUrls,
          blockExplorerUrls: ARBITRUM_CONFIG.blockExplorerUrls
        }]);
        await browserProvider.send('wallet_switchEthereumChain', [
          { chainId: ARBITRUM_CONFIG.chainIdHex }
        ]);
      } else if (err?.code === 4001) {
        throw new Error('User rejected network switch');
      } else {
        throw err;
      }
    }
  }, [selectInjectedProvider]);

  // Connect Wallet
  const connect = useCallback(async (): Promise<string | null> => {
    const injected = selectInjectedProvider();
    if (!injected) {
      console.error('No crypto wallet found');
      return null;
    }

    setIsConnecting(true);

    try {
      const browserProvider = new BrowserProvider(injected);
      await browserProvider.send('eth_requestAccounts', []);

      const network = await browserProvider.getNetwork();
      if (Number(network.chainId) !== ARBITRUM_CONFIG.chainId) {
        await switchNetwork();
      }

      const walletSigner = await browserProvider.getSigner();
      const address = await walletSigner.getAddress();

      setProvider(browserProvider);
      setSigner(walletSigner);
      setAccount(address);
      setChainId(Number(network.chainId));

      // Sync with backend session
      try {
        await fetch('/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ address })
        });
      } catch (e) {
        console.warn('Failed to sync session with backend:', e);
      }

      return address;
    } catch (e) {
      console.error('Connection failed:', e);
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, [selectInjectedProvider, switchNetwork]);

  // Disconnect Wallet
  const disconnect = useCallback(async () => {
    setSigner(null);
    setAccount(null);
    setChainId(null);

    if (providerReadOnly) {
      setProvider(providerReadOnly);
    }

    try {
      await fetch('/disconnect', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.warn('Failed to clear backend session:', e);
    }
  }, [providerReadOnly]);

  // Silent Auto-Connect
  const silentConnect = useCallback(async () => {
    const injected = selectInjectedProvider();
    if (!injected) return;

    try {
      const browserProvider = new BrowserProvider(injected);
      const accounts = await browserProvider.send('eth_accounts', []);

      if (accounts && accounts.length > 0) {
        const walletSigner = await browserProvider.getSigner();
        const address = await walletSigner.getAddress();
        const network = await browserProvider.getNetwork();

        setProvider(browserProvider);
        setSigner(walletSigner);
        setAccount(address);
        setChainId(Number(network.chainId));

        try {
          await fetch('/connect', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ address })
          });
        } catch {
          // Silently fail
        }
      }
    } catch {
      // Silently fail
    }
  }, [selectInjectedProvider]);

  // ABI Helpers
  const getAbi = useCallback((name: string): InterfaceAbi => {
    const abi = abis[name];
    if (!abi) {
      throw new Error(`ABI '${name}' not found. Available: ${Object.keys(abis).join(', ')}`);
    }
    return abi;
  }, [abis]);

  // Contract Factory
  const getContract = useCallback((
    address: string,
    abi: InterfaceAbi,
    mode: 'auto' | 'signer' | 'readOnly' = 'auto'
  ): Contract => {
    if (mode === 'readOnly') {
      if (!providerReadOnly) throw new Error('Read-only provider not initialized');
      return new Contract(address, abi, providerReadOnly);
    }
    if (mode === 'signer') {
      if (!signer) throw new Error('No signer available. Please connect wallet first.');
      return new Contract(address, abi, signer);
    }
    // Auto mode
    if (signer) return new Contract(address, abi, signer);
    if (providerReadOnly) return new Contract(address, abi, providerReadOnly);
    throw new Error('No provider available');
  }, [signer, providerReadOnly]);

  // Initialization
  useEffect(() => {
    const init = async () => {
      const roProvider = new RetryProvider(ARBITRUM_CONFIG.rpcUrls);
      setProviderReadOnly(roProvider);
      setProvider(roProvider);

      try {
        const response = await fetch('/api/abis');
        if (response.ok) {
          const data = await response.json();
          setAbis(data.abis || {});
          setContractAddress(data.contractAddress || '');
        }
      } catch (e) {
        console.warn('Failed to fetch ABIs:', e);
      }

      await silentConnect();
      setIsInitialized(true);
    };

    init();
  }, [silentConnect]);

  // Wallet Event Listeners
  useEffect(() => {
    const injected = selectInjectedProvider();
    if (!injected) return;

    const handleAccountsChanged = async (accounts: string[]) => {
      if (accounts.length === 0) {
        await disconnect();
      } else if (signer) {
        await connect();
      }
    };

    const handleChainChanged = (chainIdHex: string) => {
      const newChainId = parseInt(chainIdHex, 16);
      setChainId(newChainId);
      if (newChainId !== ARBITRUM_CONFIG.chainId) {
        window.location.reload();
      }
    };

    const handleDisconnect = () => {
      disconnect();
    };

    injected.on?.('accountsChanged', handleAccountsChanged);
    injected.on?.('chainChanged', handleChainChanged);
    injected.on?.('disconnect', handleDisconnect);

    return () => {
      injected.removeListener?.('accountsChanged', handleAccountsChanged);
      injected.removeListener?.('chainChanged', handleChainChanged);
      injected.removeListener?.('disconnect', handleDisconnect);
    };
  }, [selectInjectedProvider, signer, connect, disconnect]);

  const value: Web3ContextType = {
    provider, providerReadOnly, signer,
    account, chainId,
    isConnected: !!account, isConnecting, isInitialized,
    abis, getAbi, getContract,
    connect, disconnect, switchNetwork,
    contractAddress, network: ARBITRUM_CONFIG
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};

export const useWeb3 = (): Web3ContextType => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};
